import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { logEvent } from "@/hooks/use-event-log";
import { isProjectLockedError, toastProjectLocked } from "@/lib/db-errors";

interface AssignmentInput {
  id?: string;
  user_id: string;
  hours: number;
  label?: string | null;
  estimated_deadline?: string | null;
  notes?: string | null;
}

interface SaveAssignmentsParams {
  budgetLineId: string;
  projectId: string;
  assignments: AssignmentInput[];
}

/**
 * VIB-671: Upsert budget line assignments without losing task_status,
 * completed_at, approved_at, approved_by, rejection_reason.
 * - Rows with id → UPDATE
 * - Rows without id → INSERT
 * - Existing rows not in payload → DELETE
 * Varsler (task_assigned) opprettes av DB-trigger på insert, ikke her.
 */
export function useSaveBudgetAssignments() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async ({ budgetLineId, assignments }: SaveAssignmentsParams) => {
      const valid = assignments.filter((a) => a.hours > 0 && a.user_id);

      const { data: existing, error: selErr } = await supabase
        .from("budget_line_assignments")
        .select("id")
        .eq("budget_line_id", budgetLineId);
      if (selErr) throw new Error(`Lesing feilet: ${selErr.message}`);

      const existingIds = new Set((existing ?? []).map((r) => r.id));
      const keptIds = new Set(valid.map((a) => a.id).filter((id): id is string => !!id));

      for (const a of valid) {
        if (!a.id) continue;
        const { error: updErr } = await supabase
          .from("budget_line_assignments")
          .update({
            user_id: a.user_id,
            hours: a.hours,
            label: a.label ?? null,
            estimated_deadline: a.estimated_deadline ?? null,
            notes: a.notes ?? null,
            // VIB-1169: manuell tildeling låses så Monday-import ikke overskriver.
            hours_locked: true,
          })
          .eq("id", a.id);
        if (updErr) throw new Error(`Oppdatering feilet: ${updErr.message}`);
      }

      const newRows = valid
        .filter((a) => !a.id)
        .map((a) => ({
          budget_line_id: budgetLineId,
          user_id: a.user_id,
          hours: a.hours,
          label: a.label ?? null,
          estimated_deadline: a.estimated_deadline ?? null,
          notes: a.notes ?? null,
          // VIB-1169: manuell tildeling låses så Monday-import ikke overskriver.
          hours_locked: true,
        }));
      if (newRows.length > 0) {
        const { error: insErr } = await supabase
          .from("budget_line_assignments")
          .insert(newRows);
        if (insErr) throw new Error(`Tildeling feilet: ${insErr.message}`);
      }

      const toDelete = [...existingIds].filter((id) => !keptIds.has(id));
      if (toDelete.length > 0) {
        const { error: delErr } = await supabase
          .from("budget_line_assignments")
          .delete()
          .in("id", toDelete);
        if (delErr) throw new Error(`Sletting feilet: ${delErr.message}`);
      }
    },
    onSuccess: async (_data, variables) => {
      await invalidateFor(qc, "budget_line_assignments", { projectId: variables.projectId });
      toast.success("Timer tildelt");
      // VIB-1184 D15: assignment_created/updated/removed logges autoritativt (med
      // snapshot) av DB-triggeren log_assignment_change. Frontend-duppen fjernet.
    },
    onError: (err, vars) => {
      if (isProjectLockedError(err)) {
        toastProjectLocked({ projectId: vars.projectId, navigate });
        return;
      }
      toast.error("Noe gikk galt");
    },
  });
}

/**
 * VIB-545 / VIB-579 / VIB-681: Konverter en kostnadslinje til en oppgave.
 * Setter cost_type='internal', unit='t' og bruker antall timer som PL har
 * spesifisert (ikke auto-beregning fra beløp/timesats). Beholder amount.
 *
 * VIB-681: flagger `is_converted_from_cost=true` så admin kan filtrere på
 * konverterte linjer i reports/admin senere. Brukes også av useConvertTaskToCost
 * for å nullstille flagget hvis admin reverterer.
 */
export function useConvertCostToTask() {
  const qc = useQueryClient();
  const { activeUser } = useImpersonation();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async ({
      budgetLineId,
      projectId,
      hours,
    }: {
      budgetLineId: string;
      projectId: string;
      hours: number;
    }) => {
      const rounded = Math.round(hours * 10) / 10;
      const { data: original } = await supabase
        .from("budget_lines")
        .select("cost_type, description, amount")
        .eq("id", budgetLineId)
        .maybeSingle();

      const { error } = await supabase
        .from("budget_lines")
        .update({
          cost_type: "internal",
          unit: "t",
          quantity: rounded,
          quantity_hours: rounded,
          is_converted_from_cost: true,
        })
        .eq("id", budgetLineId);
      if (error) throw new Error(`Konvertering feilet: ${error.message}`);

      logEvent({
        event_type: "budget_line.converted_to_task",
        entity_type: "budget_line",
        entity_id: budgetLineId,
        project_id: projectId,
        user_id: activeUser?.id,
        payload: {
          previous_cost_type: original?.cost_type ?? null,
          description: original?.description ?? null,
          amount: original?.amount ?? null,
          new_hours: rounded,
        },
      });

      return { hours: rounded };
    },
    onSuccess: async (data, variables) => {
      await invalidateFor(qc, "budget_lines", { projectId: variables.projectId });
      toast.success(`Kostnad konvertert til oppgave (${data.hours}t)`);
    },
    onError: (err, vars) => {
      if (isProjectLockedError(err)) {
        toastProjectLocked({ projectId: vars.projectId, navigate });
        return;
      }
      toast.error(`Kunne ikke konvertere: ${(err as Error).message}`);
    },
  });
}

/**
 * Inverse av useConvertCostToTask: gjør en intern Oppgave-linje om til en
 * Kostnad-linje. Sletter alle assignments siden kostnad-linjer ikke skal
 * ha tildelinger. Beholder amount (salgssum). Time_entries beholdes som
 * historisk logg — de er fremdeles linket via budget_line_id.
 *
 * Sølvrev-modell: cost_type avledes fra estimatedCost.
 *   - estimatedCost > 0  → ekstern kostnad (innleid leverandør)
 *   - estimatedCost == 0 → intern kostnad (utleie av eget utstyr e.l.,
 *                          ingen direkte innkjøpskostnad)
 */
export function useConvertTaskToCost() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async ({
      budgetLineId,
      projectId: _projectId,
      estimatedCost,
    }: {
      budgetLineId: string;
      projectId: string;
      estimatedCost: number;
    }) => {
      const { error: delErr } = await supabase
        .from("budget_line_assignments")
        .delete()
        .eq("budget_line_id", budgetLineId);
      if (delErr) throw new Error(`Kunne ikke fjerne tildelinger: ${delErr.message}`);

      const newCostType = estimatedCost > 0 ? "external" : "internal";

      const { error } = await supabase
        .from("budget_lines")
        .update({
          cost_type: newCostType,
          unit: "stk",
          quantity: 1,
          quantity_hours: 0,
          estimated_cost: estimatedCost,
          // VIB-681: nullstill konverterings-flagg ved revert
          is_converted_from_cost: false,
        })
        .eq("id", budgetLineId);
      if (error) throw new Error(`Konvertering feilet: ${error.message}`);

      return { costType: newCostType };
    },
    onSuccess: async (data, variables) => {
      await invalidateFor(qc, "budget_lines", { projectId: variables.projectId });
      await invalidateFor(qc, "budget_line_assignments", { projectId: variables.projectId });
      const label = data.costType === "external" ? "ekstern kostnad" : "intern kostnad";
      toast.success(`Oppgaven ble konvertert til ${label}`);
    },
    onError: (err, vars) => {
      if (isProjectLockedError(err)) {
        toastProjectLocked({ projectId: vars.projectId, navigate });
        return;
      }
      toast.error(`Kunne ikke konvertere: ${(err as Error).message}`);
    },
  });
}

interface DeleteAssignmentParams {
  assignmentId: string;
  projectId: string;
}

/**
 * Admin Timer-tab: sletter en enkelt budget_line_assignment via id.
 * Bruker invalidate på project-detail og logger admin-event.
 */
export function useDeleteAssignment() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async ({ assignmentId }: DeleteAssignmentParams) => {
      const { error } = await supabase
        .from("budget_line_assignments")
        .delete()
        .eq("id", assignmentId);
      if (error) throw new Error(error.message);
    },
    onSuccess: async (_data, vars) => {
      await invalidateFor(qc, "budget_line_assignments", {
        projectId: vars.projectId,
        assignmentId: vars.assignmentId,
      });
      // VIB-1184 D15: assignment_removed (med snapshot) logges av DB-triggeren
      // log_assignment_change. Frontend-duppen fjernet.
    },
    onError: (err, vars) => {
      if (isProjectLockedError(err)) {
        toastProjectLocked({ projectId: vars.projectId, navigate });
        return;
      }
      toast.error(`Sletting feilet: ${(err as Error).message}`);
    },
  });
}

interface CreateAssignmentParams {
  budget_line_id: string;
  user_id: string;
  hours: number;
  label?: string | null;
  projectId: string;
}

/**
 * Admin Timer-tab: oppretter en enkelt budget_line_assignment.
 * Retur-verdien inneholder ny id slik at admin-UI kan binde rader umiddelbart
 * uten å vente på invalidate-roundtrip.
 */
export function useCreateAssignment() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (params: CreateAssignmentParams) => {
      const { data, error } = await supabase
        .from("budget_line_assignments")
        .insert({
          budget_line_id: params.budget_line_id,
          user_id: params.user_id,
          hours: params.hours,
          label: params.label ?? null,
          // VIB-1169: manuell tildeling låses så Monday-import ikke overskriver.
          hours_locked: true,
        })
        .select()
        .single();
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: async (data, vars) => {
      await invalidateFor(qc, "budget_line_assignments", {
        projectId: vars.projectId,
        userId: vars.user_id,
        assignmentId: (data as { id: string }).id,
      });
      // VIB-1184 D15: assignment_created (med snapshot) logges av DB-triggeren
      // log_assignment_change. Frontend-duppen fjernet.
    },
    onError: (err, vars) => {
      if (isProjectLockedError(err)) {
        toastProjectLocked({ projectId: vars.projectId, navigate });
        return;
      }
      toast.error(`Opprettelse feilet: ${(err as Error).message}`);
    },
  });
}
